import { ReactNode, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { orderBy } from 'lodash';

import {
  useAsyncEffect,
  Utils,
  DateTools,
  Tooltip,
  Icon,
  TTableColumn,
  Table,
  EmptyState,
  Pagination,
} from '@treezor/design-system';

import { searchPredefRestrictionsGroups } from '@/store/actions/predef-restrictions-group.actions';
import { RestrictionsGroupStatus, TRestrictionsGroupType } from '@/store/models/restriction.model';

import { RestrictionGroupActions } from '@/pages/admin/restrictionGroup/actions/RestrictionGroupActions';

import { DashboardHelpers } from '@/utils/DashboardHelpers';

import { useAppSelector, useAppDispatch } from '@/hooks/redux-helpers';

import { selectPredefRestrictionGroupsByType } from '@/store/selectors';
import { TableSkeleton } from '@/ui-kit/loaders/TableSkeleton';

export const ITEM_PER_PAGE = 50;

const tableColumns = ['status', 'name', 'date', 'type', 'actions'];

type TRestrictionGroupCell = Record<(typeof tableColumns)[number], ReactNode>;

export const RestrictionsGroupTab = () => {
  const { type } = useParams<{ type: TRestrictionsGroupType }>();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const config = useAppSelector((state) => state.dashboardConfig.config);

  const {
    elements: restrictionsGroups,
    pagination,
    isLoading,
    error,
  } = useAppSelector((state) => selectPredefRestrictionGroupsByType(state, type ?? 'mid'));

  useAsyncEffect(async () => {
    if (type && config.settings.cards.restrictions?.length)
      dispatch(searchPredefRestrictionsGroups({ types: [type], currentPage: 1 }));
  }, [type, dispatch]);

  const onPageChange = useCallback(
    (currentPage: number) => {
      if (type) dispatch(searchPredefRestrictionsGroups({ types: [type], currentPage }));
    },
    [dispatch, type],
  );

  const headers: TTableColumn<TRestrictionGroupCell>[] = tableColumns.map((field) => ({
    name: field,
    field,
    label: t(`pages.currentProfiles.admin.restrictions.table.headers.${field}`),
  }));

  const rows = orderBy(
    restrictionsGroups?.map((restriction) => restriction.group),
    ['startDate'],
    ['desc'],
  ).map((restrictionsGroup) => ({
    status:
      restrictionsGroup.status === RestrictionsGroupStatus.VALIDATED ? (
        <Tooltip messageContent={t('pages.cores.status.VALIDATED')} placement="right">
          <Icon name="check_circle" className="text-icon-success" />
        </Tooltip>
      ) : restrictionsGroup.status === RestrictionsGroupStatus.PENDING ? (
        <Tooltip messageContent={t('pages.cores.status.PENDING')} placement="right">
          <Icon name="pause" className="text-icon-warning" />
        </Tooltip>
      ) : (
        <Tooltip messageContent={t('pages.cores.status.CANCELED')} placement="right">
          <Icon name="block" className="text-icon-error" />
        </Tooltip>
      ),
    name: Utils.displayNameRestrictionUi(restrictionsGroup.name),
    date: DateTools.format(restrictionsGroup.startDate),
    type: restrictionsGroup.isWhitelist ? 'Whitelist' : 'BlackList',
    actions: (
      <RestrictionGroupActions
        isDisabled={restrictionsGroup.status === RestrictionsGroupStatus.CANCELED}
        restrictionsGroup={restrictionsGroup}
      />
    ),
  }));

  if (error) return <div className="text-error text-center m-11">{DashboardHelpers.getErrorMessage(error)} </div>;

  return (
    <div className="flex flex-col w-full py-5 gap-5">
      {isLoading ? (
        <TableSkeleton />
      ) : (
        <Table
          columns={headers}
          rows={rows}
          className="bg-primary"
          emptyComponent={
            <EmptyState title={t('components.molecules.table.noRecord')} icon="search_v1" size="m" className="p-6" />
          }
        />
      )}
      {pagination?.totalPages > 0 && (
        <Pagination
          {...pagination}
          itemCount={pagination.totalPages * ITEM_PER_PAGE}
          itemPerPage={ITEM_PER_PAGE}
          onPageChange={(item) => onPageChange(Number(item.selected) + 1)}
          isFirstButtonVisible
          isLastButtonVisible
          className="flex justify-end w-full"
        />
      )}
    </div>
  );
};
